<script>
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import i18n from '@/i18n'

/**
 * Every dropdown in this app.
 *
 * ── Why a wrapper ───────────────────────────────────────────────────────
 * vue-multiselect works in OPTION OBJECTS: `:value` is `{text, value}` and
 * `@input` hands one back. Every page that used it in one_sales therefore
 * carried a `selectedX` mirror of its own form field plus an `onXChange`
 * handler to keep the two in step — about eight lines per dropdown, and the
 * bug that comes with them is a form seeded from the API whose dropdown shows
 * blank because only one of the pair was set.
 *
 * This component speaks VALUES. `v-model` is the id (or an array of ids for a
 * multi-select) and the object round-trip happens here, once.
 *
 * ── Why everything is a multiselect now, including single-value fields ──
 * Consistency of behaviour, not just of looks: a native `<select>` cannot be
 * typed into, and the brand picker on a platform with forty brands is unusable
 * without search. Single-value fields pass `:multiple="false"` and get the same
 * search box.
 *
 * ── The empty case ──────────────────────────────────────────────────────
 * An empty array means "no filter", which is the same thing the old "All"
 * option meant — so there is no All option. One that had to be deselected
 * before a real choice registered would be a control fighting the user, and
 * the server drops the sentinel anyway (FilterValues::many).
 */
export default {
  name: 'MultiSelect',
  components: { Multiselect },
  props: {
    /** The id, or an array of ids when `multiple`. */
    value: { type: [String, Number, Array, Boolean], default: null },

    /** `[{ text, value }]`. `text`/`value` are the keys the app's ddlHelper
     *  emits; override with labelKey/valueKey for an API list. */
    options: { type: Array, default: () => [] },

    labelKey: { type: String, default: 'text' },
    valueKey: { type: String, default: 'value' },

    multiple: { type: Boolean, default: true },
    searchable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },

    /** A single-select that may be cleared back to null. Filters are always
     *  clearable; a required form field is not. */
    allowEmpty: { type: Boolean, default: true },

    placeholder: { type: String, default: null },

    /** Red border, for a vuelidate `$error`. */
    invalid: { type: Boolean, default: false },

    /** Rendered under the box, e.g. "Only unlinked agents are listed." */
    hint: { type: String, default: null },

    id: { type: String, default: null },
  },
  computed: {
    /**
     * The option object(s) vue-multiselect wants, derived from `value`.
     *
     * Compared with String(): a tenant id arrives as a number from the API and
     * as a string from a query parameter, and `===` on those is false — which
     * shows as a filter chip that will not appear after a page reload.
     */
    selected() {
      if (this.multiple) {
        const values = Array.isArray(this.value) ? this.value : []
        return values
          .map((v) => this.optionFor(v))
          .filter((o) => o !== undefined)
      }

      return this.optionFor(this.value)
    },
    resolvedPlaceholder() {
      if (this.placeholder !== null) return this.placeholder
      return this.multiple ? i18n.t('label.selectAny') : i18n.t('label.select')
    },
  },
  methods: {
    optionFor(value) {
      if (value === null || value === undefined || value === '') return undefined

      return this.options.find((o) => String(o[this.valueKey]) === String(value))
    },
    label(option) {
      return option ? option[this.labelKey] : ''
    },
    onInput(selection) {
      if (this.multiple) {
        this.$emit('input', (selection || []).map((o) => o[this.valueKey]))
      } else {
        // null, not '', when cleared: the services treat null as "leave this
        // key out of the request" and '' as a value to send.
        this.$emit('input', selection ? selection[this.valueKey] : null)
      }

      this.$emit('change', selection)
    },
  },
}
</script>

<template>
  <div>
    <multiselect
      :id="id"
      :value="selected"
      :options="options"
      :multiple="multiple"
      :searchable="searchable"
      :disabled="disabled"
      :allow-empty="multiple ? true : allowEmpty"
      :close-on-select="!multiple"
      :clear-on-select="false"
      :custom-label="label"
      :track-by="valueKey"
      :placeholder="resolvedPlaceholder"
      :class="{ 'is-invalid-multiselect': invalid }"
      :show-labels="false"
      open-direction="below"
      @input="onInput"
    >
      <!-- vue-multiselect's own empty text is "No elements found. Consider
           changing the search query." which reads as a developer message. -->
      <template slot="noResult">{{ $t('label.noResultsFound') }}</template>
      <template slot="noOptions">{{ $t('label.noOptions') }}</template>
    </multiselect>
    <small v-if="hint" class="form-text text-muted">{{ hint }}</small>
  </div>
</template>

<style>
/*
 * vue-multiselect renders its own bordered div, so bootstrap's `.is-invalid`
 * (which styles the element it sits on) has nothing to attach to. This is the
 * same red as .form-control.is-invalid.
 */
.is-invalid-multiselect .multiselect__tags {
  border-color: #f46a6a;
}

/* Inside a `.form-control-sm` row the default 40px control is a head taller
   than the inputs beside it. */
.multiselect--sm .multiselect__tags {
  min-height: 31px;
  padding-top: 4px;
  font-size: 0.8125rem;
}
</style>
